/**
 * Job Activities Hooks
 * React Query hooks for fetching and managing job activities
 *
 * Following DDD Principles:
 * - Hooks consume job-activities service
 * - React Query handles caching and state management
 * - Components receive DTOs ready for display
 * - NO business logic in hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobActivitiesService } from '@/services/job-activities.service';
import type { JobActivityDto, LogJobActivityRequest } from '../types/activity';

/**
 * Job Activity query key factory
 * Provides consistent cache keys for React Query
 */
export const jobActivityKeys = {
  all: ['job-activities'] as const,
  lists: () => [...jobActivityKeys.all, 'list'] as const,
  list: (jobId: string, types?: string[]) => [...jobActivityKeys.lists(), jobId, { types }] as const,
};

/**
 * Hook to fetch job activities
 * @param jobId - The job ID
 * @param types - Optional array of activity types to filter by
 */
export function useJobActivities(jobId: string, types?: string[]) {
  return useQuery<JobActivityDto[], Error>({
    queryKey: jobActivityKeys.list(jobId, types),
    queryFn: () => jobActivitiesService.getJobActivities(jobId, types),
    staleTime: 1 * 60 * 1000, // 1 minute - activities change frequently
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    enabled: !!jobId,
  });
}

/**
 * Hook to log a new job activity
 * @param jobId - The job ID
 */
export function useLogJobActivity(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: LogJobActivityRequest) =>
      jobActivitiesService.logActivity(jobId, request),
    onSuccess: () => {
      // Invalidate all activity queries for this job
      queryClient.invalidateQueries({ queryKey: jobActivityKeys.lists() });
    },
  });
}
